import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { cmApi, type BettickerMapping } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'

const QUERY_KEY = ['cm', 'book', 'betticker-mapping'] as const

/** Lab ColorItems — screenshot / mapping badges. */
export const TICKER_FLAG_COLORS = {
  smart: '#00ff2b',
  doubtfull: '#f4dc11',
  watchlist: '#ee1620',
} as const

export type TickerMemberFlag = keyof typeof TICKER_FLAG_COLORS

/** Lab getBettickerMapping — Smart / Doubtful / Watchlist lists. */
export function useBettickerMapping(enabled = true) {
  return useQuery({
    queryKey: QUERY_KEY,
    enabled,
    staleTime: 30_000,
    queryFn: () => cmApi.getBettickerMapping(authHeaders()),
  })
}

/** Lab updateSmartUser — add/remove a Smart Member (CM owner only). */
export function useUpdateSmartUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { playerName: string; smartUser: boolean }) =>
      cmApi.updateSmartUser(authHeaders(), body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: QUERY_KEY })
    },
  })
}

/** Lab updateBettickerMapping — replace doubtful/watchlist name+IP lists. */
export function useUpdateBettickerMapping() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      mappedDoubtfullUsers?: string[]
      mappedDoubtfullIps?: string[]
      mappedWatchlistUsers?: string[]
      mappedWatchlistIps?: string[]
    }) => cmApi.updateBettickerMapping(authHeaders(), body),
    onSuccess: async (res) => {
      qc.setQueryData(QUERY_KEY, res)
      await qc.invalidateQueries({ queryKey: QUERY_KEY })
    },
  })
}

function inList(value: string | undefined | null, list: string[] | undefined): boolean {
  const key = (value || '').trim().toLowerCase()
  if (!key || !list?.length) return false
  return list.some((u) => u.trim().toLowerCase() === key)
}

export function isSmartMember(
  username: string | undefined | null,
  mappedSmartUsers: string[] | undefined,
): boolean {
  return inList(username, mappedSmartUsers)
}

/** Lab getAllBets overlay — username or IP match. */
export function isDoubtfullMember(
  username: string | undefined | null,
  clientIp: string | undefined | null,
  mapping: Pick<BettickerMapping, 'mappedDoubtfullUsers' | 'mappedDoubtfullIps'> | undefined,
): boolean {
  if (!mapping) return false
  return (
    inList(username, mapping.mappedDoubtfullUsers) || inList(clientIp, mapping.mappedDoubtfullIps)
  )
}

export function isWatchlistMember(
  username: string | undefined | null,
  clientIp: string | undefined | null,
  mapping: Pick<BettickerMapping, 'mappedWatchlistUsers' | 'mappedWatchlistIps'> | undefined,
): boolean {
  if (!mapping) return false
  return (
    inList(username, mapping.mappedWatchlistUsers) || inList(clientIp, mapping.mappedWatchlistIps)
  )
}

/**
 * Lab getAllBets member/row color — prefer bet stamps (smartUser /
 * isSmartUserByCron / doubtfull / watchlist), then mapping lists:
 * smart > doubtful > watchlist.
 *
 * Lab `text-blue` also fires when `isSmartUserByCron && smartUserComment`.
 */
export function resolveTickerMemberFlag(
  username: string | undefined | null,
  clientIp: string | undefined | null,
  mapping: BettickerMapping | undefined,
  stamps?: {
    smartUser?: boolean | null
    isSmartUserByCron?: boolean | null
    doubtfull?: boolean | null
    watchlist?: boolean | null
  },
): TickerMemberFlag | null {
  if (stamps?.smartUser || stamps?.isSmartUserByCron) return 'smart'
  if (stamps?.doubtfull) return 'doubtfull'
  if (stamps?.watchlist) return 'watchlist'
  if (!mapping) return null
  if (isSmartMember(username, mapping.mappedSmartUsers)) return 'smart'
  if (isDoubtfullMember(username, clientIp, mapping)) return 'doubtfull'
  if (isWatchlistMember(username, clientIp, mapping)) return 'watchlist'
  return null
}

export function tickerFlagLabel(flag: TickerMemberFlag): string {
  if (flag === 'smart') return 'Smart Members'
  if (flag === 'doubtfull') return 'Doubtfull Members'
  return 'Watchlist Members'
}
